import { z } from 'zod'

// ─── Feedback (評価) ─────────────────────────────────────
export const CreateFeedbackSchema = z.object({
  rating: z.number().int().min(1).max(5),
  comment: z.string().optional(),
})

export const UpdateFeedbackSchema = z.object({
  rating: z.number().int().min(1).max(5).optional(),
  comment: z.string().optional(),
})

export type CreateFeedbackInput = z.infer<typeof CreateFeedbackSchema>
export type UpdateFeedbackInput = z.infer<typeof UpdateFeedbackSchema>

// ─── Question (質問) ─────────────────────────────────────
export const CreateQuestionSchema = z.object({
  question: z.string().min(1),
  videoTimestamp: z.number().int().positive().optional(),
})

export const UpdateQuestionSchema = z.object({
  question: z.string().min(1).optional(),
  isResolved: z.boolean().optional(),
})

export type CreateQuestionInput = z.infer<typeof CreateQuestionSchema>
export type UpdateQuestionInput = z.infer<typeof UpdateQuestionSchema>

// ─── Answer (回答) ───────────────────────────────────────
export const CreateAnswerSchema = z.object({
  answer: z.string().min(1),
})

export const UpdateAnswerSchema = z.object({
  answer: z.string().min(1),
})

export type CreateAnswerInput = z.infer<typeof CreateAnswerSchema>
export type UpdateAnswerInput = z.infer<typeof UpdateAnswerSchema>

// ─── Response schemas (GET レスポンス形状) ───────────────
const dateOrString = z.union([z.date(), z.string()])

export const FeedbackResponseSchema = z.object({
  feedback: z.object({
    id: z.string(),
    userId: z.string(),
    lessonId: z.string(),
    rating: z.number().int().min(1).max(5),
    comment: z.string().nullable(),
    createdAt: dateOrString,
    updatedAt: dateOrString,
    tenantId: z.string().nullable(),
  }),
  userName: z.string().nullable(),
})

export const QuestionResponseSchema = z.object({
  question: z.object({
    id: z.string(),
    lessonId: z.string(),
    userId: z.string(),
    question: z.string(),
    videoTimestamp: z.number().nullable(),
    isResolved: z.boolean(),
    upvotes: z.number(),
    createdAt: dateOrString,
    updatedAt: dateOrString,
    tenantId: z.string().nullable(),
  }),
  userName: z.string().nullable(),
  answers: z
    .array(
      z.object({
        answer: z.object({
          id: z.string(),
          questionId: z.string(),
          userId: z.string(),
          answer: z.string(),
          isInstructor: z.boolean(),
          isAccepted: z.boolean(),
          upvotes: z.number(),
          createdAt: dateOrString,
          updatedAt: dateOrString,
          tenantId: z.string().nullable(),
        }),
        userName: z.string().nullable(),
      }),
    )
    .optional(),
})

export const AnswerResponseSchema = z.object({
  answer: z.object({
    id: z.string(),
    questionId: z.string(),
    userId: z.string(),
    answer: z.string(),
    isInstructor: z.boolean(),
    isAccepted: z.boolean(),
    upvotes: z.number(),
    createdAt: dateOrString,
    updatedAt: dateOrString,
    tenantId: z.string().nullable(),
  }),
  userName: z.string().nullable(),
})

export const RatingSummarySchema = z.object({
  average: z.number(),
  count: z.number(),
  distribution: z.record(z.coerce.number(), z.number()),
})

export type FeedbackResponse = z.infer<typeof FeedbackResponseSchema>
export type QuestionResponse = z.infer<typeof QuestionResponseSchema>
export type AnswerResponse = z.infer<typeof AnswerResponseSchema>
export type RatingSummary = z.infer<typeof RatingSummarySchema>
